// Data hooks (TanStack Query). The UI renders what the server returns —
// capabilities, allowed transitions and serving reasons are never computed here.

import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { api, call, csrf } from "./client";
import { ApiProblem } from "./errors";
import type { Page } from "./types";

export const keys = {
  me: ["me"] as const,
  memberships: ["memberships"] as const,
  frontDesk: (orgId: string) => ["org", orgId, "front-desk"] as const,
  org: (orgId: string) => ["org", orgId] as const,
};

/** Current user + session, or null when signed out (401). */
export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: async () => {
      try {
        const me = await call(() => api.GET("/me"));
        csrf.set(me.csrfToken);
        return me;
      } catch (error) {
        if (error instanceof ApiProblem && error.status === 401) {
          csrf.set(null);
          return null;
        }
        throw error;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
}

export function useMemberships(enabled = true) {
  return useQuery({
    queryKey: keys.memberships,
    queryFn: async () => (await call(() => api.GET("/me/memberships"))).memberships,
    enabled,
  });
}

export function useFrontDesk(orgId: string) {
  return useQuery({
    queryKey: keys.frontDesk(orgId),
    queryFn: () => call(() => api.GET("/orgs/{orgId}/front-desk", { params: { path: { orgId } } })),
  });
}

/** Cursor-paginated collection with "load more". */
export function usePaged<T>(
  key: QueryKey,
  fetchPage: (cursor: string | undefined) => Promise<Page<T>>,
  enabled = true
) {
  const query = useInfiniteQuery({
    queryKey: key,
    queryFn: ({ pageParam }) => fetchPage(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled,
  });
  return { ...query, items: query.data?.pages.flatMap((p) => p.items) ?? [] };
}

/**
 * Mutation that invalidates the organization's cached data on success (the
 * server is the source of truth: state, sequence and reasons are re-read).
 */
export function useOrgMutation<TInput, TOutput>(orgId: string, run: (input: TInput) => Promise<TOutput>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: keys.org(orgId) });
    },
  });
}
